export const CaseStudyExamples = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Real-World Examples</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            T12 Case Studies: Success Stories and Lessons Learned
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            See how real apartment building owners used T12 optimization to maximize their sale prices or learned expensive lessons from T12 mistakes.
          </p>
        </div>
        
        <div className="space-y-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-trophy-line text-3xl text-green-600"></i>
              </div>
              <div>
                <div className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full mb-2">SUCCESS STORY</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">The 12-Month Optimization Strategy</h3>
                <p className="text-gray-600">32-unit apartment building in suburban market</p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Situation</h4>
                <p className="text-gray-700 mb-4">
                  Owner planned to sell within 18 months but current T12 showed weak performance:
                </p>
                <ul className="space-y-2 text-sm text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>12% vacancy rate (market average: 6%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>Rents 15% below market rates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>High utility costs due to old fixtures</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>No additional income sources</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>T12 NOI: $185,000</span>
                  </li>
                </ul>
                
                <h4 className="font-bold text-gray-900 mb-4">Actions Taken Over 12 Months</h4>
                <div className="space-y-3">
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-1">Months 1-3: Foundation</div>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Installed water-saving fixtures ($8,000 investment)</li>
                      <li>• Upgraded to LED lighting ($3,000 investment)</li>
                      <li>• Implemented pet fee policy ($35/pet/month)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-1">Months 4-8: Growth</div>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Raised rents 10% at lease renewals</li>
                      <li>• Aggressive marketing reduced vacancy to 5%</li>
                      <li>• Added parking fees ($50/space/month)</li>
                      <li>• Negotiated lower insurance rate</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-1">Months 9-12: Optimization</div>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Continued rent increases to market rate</li>
                      <li>• Maintained low vacancy through retention</li>
                      <li>• Documented all improvements</li>
                      <li>• Prepared professional T12 package</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Results</h4>
                
                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200 mb-6">
                  <h5 className="font-bold text-gray-900 mb-4">New T12 Performance</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Rental Income Increase:</span>
                      <span className="font-bold text-green-600">+$62,400/year</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">New Income Sources:</span>
                      <span className="font-bold text-green-600">+$18,000/year</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Reduced Vacancy Loss:</span>
                      <span className="font-bold text-green-600">+$28,800/year</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Expense Reductions:</span>
                      <span className="font-bold text-green-600">+$12,000/year</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 border-green-300 pt-3 mt-3">
                      <span className="font-bold text-gray-900">Total NOI Increase:</span>
                      <span className="text-2xl font-bold text-green-600">+$121,200</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">New T12 NOI:</span>
                      <span className="text-2xl font-bold text-gray-900">$306,200</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                  <h5 className="font-bold mb-4">Financial Impact</h5>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span>Original Expected Value:</span>
                      <span className="font-bold">$2,846,154</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Actual Sale Price:</span>
                      <span className="font-bold">$4,710,769</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 border-white/30 pt-3 mt-3">
                      <span className="font-bold">Value Increase:</span>
                      <span className="text-2xl font-bold">$1,864,615</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Total Investment:</span>
                      <span className="font-bold">$11,000</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 border-white/30 pt-3 mt-3">
                      <span className="font-bold">Net Gain:</span>
                      <span className="text-3xl font-bold">$1,853,615</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>ROI on Investment:</span>
                      <span className="text-xl font-bold">16,851%</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Key Takeaway:</strong> By investing $11,000 and 12 months of focused effort, the owner increased property value by $1.86 million. The improved T12 attracted multiple offers and closed 30% above the original expected price.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-red-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-3xl text-red-600"></i>
              </div>
              <div>
                <div className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full mb-2">CAUTIONARY TALE</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">The Cost of Poor T12 Documentation</h3>
                <p className="text-gray-600">18-unit apartment building in urban market</p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Situation</h4>
                <p className="text-gray-700 mb-4">
                  Owner decided to sell quickly and rushed to compile T12 documentation:
                </p>
                <ul className="space-y-2 text-sm text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Incomplete monthly records (missing 3 months)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Used scheduled rent instead of collected rent</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Didn't include property management fees</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Mixed personal and property expenses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                    <span>Couldn't provide supporting documentation</span>
                  </li>
                </ul>
                
                <h4 className="font-bold text-gray-900 mb-4">What Happened</h4>
                <div className="space-y-3">
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Week 1: Initial Interest</div>
                    <p className="text-sm text-gray-700">Listed at $2.4M based on claimed T12 NOI of $156,000. Received 5 inquiries from serious buyers.</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Week 2-3: Due Diligence Begins</div>
                    <p className="text-sm text-gray-700">Buyers requested bank statements and supporting docs. Owner couldn't provide complete records. 3 buyers walked away immediately.</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Week 4-6: Problems Discovered</div>
                    <p className="text-sm text-gray-700">Remaining buyers found discrepancies between T12 and bank statements. Actual collected rent was $42,000 less than claimed. Expenses understated by $18,000.</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Week 7-8: Renegotiation</div>
                    <p className="text-sm text-gray-700">Last 2 buyers submitted revised offers based on adjusted NOI. Both offers significantly lower than asking price.</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Financial Impact</h4>
                
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200 mb-6">
                  <h5 className="font-bold text-gray-900 mb-4">Buyer's Adjusted Analysis</h5>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Claimed T12 NOI:</span>
                      <span className="font-bold text-gray-900">$156,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Actual Collected Rent Adjustment:</span>
                      <span className="font-bold text-red-600">-$42,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Missing Expense Adjustment:</span>
                      <span className="font-bold text-red-600">-$18,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Add Management Fees (8%):</span>
                      <span className="font-bold text-red-600">-$20,800</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Deferred Maintenance Reserve:</span>
                      <span className="font-bold text-red-600">-$15,000</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 border-red-300 pt-3 mt-3">
                      <span className="font-bold text-gray-900">Buyer's Underwriting NOI:</span>
                      <span className="text-xl font-bold text-red-600">$60,200</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white mb-6">
                  <h5 className="font-bold mb-4">Final Outcome</h5>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span>Original Asking Price:</span>
                      <span className="font-bold">$2,400,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Expected Value (claimed NOI):</span>
                      <span className="font-bold">$2,400,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Highest Revised Offer:</span>
                      <span className="font-bold text-red-400">$926,154</span>
                    </div>
                    <div className="flex justify-between items-center border-t-2 border-white/30 pt-3 mt-3">
                      <span className="font-bold">Lost Value:</span>
                      <span className="text-2xl font-bold text-red-400">-$1,473,846</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Percentage Loss:</span>
                      <span className="text-xl font-bold text-red-400">-61%</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h5 className="font-bold text-gray-900 mb-3">What Happened Next</h5>
                  <p className="text-sm text-gray-700 mb-3">
                    Owner rejected both offers and took property off market. Spent 6 months:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                      <span>Reconstructing accurate T12 from bank records</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                      <span>Improving property operations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                      <span>Building clean documentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                      <span>Eventually sold for $1.65M (still $750K below original expectations)</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Key Lesson:</strong> Poor T12 documentation cost this owner $750,000+ in lost value and 6 months of delays. The damage to credibility from the first failed listing attempt permanently affected buyer perception and final sale price.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-3xl text-blue-600"></i>
              </div>
              <div>
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-2">QUICK WIN</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">The Power of Transparency</h3>
                <p className="text-gray-600">24-unit apartment building with challenges</p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Challenge</h4>
                <p className="text-gray-700 mb-4">
                  Property had legitimate issues that showed in T12:
                </p>
                <ul className="space-y-2 text-sm text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>15% vacancy due to major renovation in 3 units</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>Higher than normal maintenance from aging HVAC systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>One-time legal expense from tenant dispute</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-600 mt-0.5"></i>
                    <span>T12 NOI: $142,000 (below market expectations)</span>
                  </li>
                </ul>
                
                <h4 className="font-bold text-gray-900 mb-4">The Approach</h4>
                <p className="text-gray-700 mb-4">
                  Instead of hiding issues, owner provided:
                </p>
                <div className="space-y-3">
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Complete T12 with Explanations</div>
                    <p className="text-sm text-gray-700">Detailed notes explaining each anomaly with supporting documentation</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Normalized T12 Analysis</div>
                    <p className="text-sm text-gray-700">Showed what NOI would be without one-time expenses and at stabilized occupancy</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Renovation Documentation</div>
                    <p className="text-sm text-gray-700">Photos and invoices showing $45,000 in recent unit upgrades that would command higher rents</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">Forward-Looking Plan</div>
                    <p className="text-sm text-gray-700">Clear path to $180,000+ NOI once renovated units leased and HVAC issues resolved</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-4">The Outcome</h4>
                
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-6 border border-blue-200 mb-6">
                  <h5 className="font-bold text-gray-900 mb-4">Buyer Response</h5>
                  <p className="text-sm text-gray-700 mb-4">
                    Transparency and documentation impressed buyers. Instead of walking away from challenges, they saw opportunity.
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Current T12 NOI:</span>
                      <span className="font-bold text-gray-900">$142,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Normalized NOI (buyer's calc):</span>
                      <span className="font-bold text-blue-600">$175,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Value at 6.5% cap:</span>
                      <span className="font-bold text-blue-600">$2,692,308</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200 mb-6">
                  <h5 className="font-bold text-gray-900 mb-4">Final Results</h5>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Number of Offers:</span>
                      <span className="font-bold text-gray-900">3 competitive offers</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Final Sale Price:</span>
                      <span className="font-bold text-green-600">$2,750,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Days on Market:</span>
                      <span className="font-bold text-gray-900">42 days</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Buyer Type:</span>
                      <span className="font-bold text-gray-900">Value-add investor</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Why It Worked:</strong>
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Complete transparency built trust</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Documentation proved issues were temporary</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Buyers could underwrite future potential</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Attracted right buyer type (value-add investor)</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Key Takeaway:</strong> Honesty about T12 challenges, combined with thorough documentation and clear explanations, can actually increase buyer confidence and sale price. The right buyer will see opportunity where others see problems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};